/**
 * Parses an optional recommendation block from assistant text.
 *
 * Model format (own line, case-insensitive label; markdown wrappers tolerated):
 *   RECOMMENDATION: mri | Lumbar spine | An MRI gives the clearest view…
 *   RECOMMENDATION: mri | Lumbar spine + Cervical spine | Multi-region… | Manchester
 *
 * Fields: product_id | body_part_label(s) | why [| area]
 * Multiple body parts are separated with " + " and capped by the product max.
 * product_id must resolve via the curated catalog (or its aliases).
 *
 * Deep-link slug resolution against the live body-part catalog happens on the
 * server when the bubble finalises. Client resolution is preview-only and must
 * not invent SEO search URLs (those can be wrong for sided / multi-part labels).
 */

const RECOMMENDATION_LINE =
  /(?:^|\n)\s*[`*_]*RECOMMENDATION[`*_]*:[`*_]*\s*([^\n|]+)\|([^\n|]+)\|([^\n|]+?)(?:\|([^\n]+?))?\s*(?:\n|$)/i;
const MAX_WHY_LENGTH = 320;
const MAX_BODY_PART_LENGTH = 80;
const MAX_AREA_LENGTH = 64;
const BODY_PART_SEPARATOR = /\s*\+\s*/;
const BODY_PART_DISPLAY_JOINER = " + ";
const DEFAULT_MAX_BODY_PARTS = 1;
const DEFAULT_AREA_SLUG = "london";
const PRIMARY_CTA_BOOK = "book";
// Matches MRI/CT booking limits when no product catalog is available yet.
const PERMISSIVE_MAX_BODY_PARTS = 4;

export function normalizeProductId(productId, aliases = {}) {
  const key = (productId || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");

  if (!key) {
    return key;
  }

  return aliases[key] || key;
}

export function normalizeArea(area, defaultArea = DEFAULT_AREA_SLUG) {
  const cleaned = (area || "").toString().trim().slice(0, MAX_AREA_LENGTH);
  if (!cleaned) {
    return defaultArea;
  }

  const slug = cleaned
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || defaultArea;
}

export function parseBodyParts(
  bodyPartField,
  maxBodyParts = DEFAULT_MAX_BODY_PARTS
) {
  const limit = Math.max(Number(maxBodyParts) || DEFAULT_MAX_BODY_PARTS, 1);

  return (bodyPartField || "")
    .toString()
    .split(BODY_PART_SEPARATOR)
    .map((part) => part.trim().slice(0, MAX_BODY_PART_LENGTH))
    .filter((part) => part.length > 0)
    .filter((part, index, parts) => parts.indexOf(part) === index)
    .slice(0, limit);
}

/**
 * @param {string} [rawText]
 * @param {{ products?: Record<string, any> | null, aliases?: Record<string, string> }} [catalog]
 */
export function parseRecommendation(
  rawText,
  { products = null, aliases = {} } = {}
) {
  const text = (rawText || "").toString();
  const match = text.match(RECOMMENDATION_LINE);

  if (!match) {
    return { displayText: text, recommendation: null };
  }

  const productId = normalizeProductId(match[1], aliases);
  const product = products?.[productId];
  // When no catalog is supplied, allow the MRI-style maximum so multi-part
  // lines are preserved for a later resolve step.
  const maxBodyParts =
    product?.maxBodyParts ??
    (products == null ? PERMISSIVE_MAX_BODY_PARTS : DEFAULT_MAX_BODY_PARTS);
  const bodyParts = parseBodyParts(match[2], maxBodyParts);
  const bodyPart = bodyParts.join(BODY_PART_DISPLAY_JOINER);
  const why = match[3].trim().slice(0, MAX_WHY_LENGTH);
  const area = match[4]
    ? normalizeArea(match[4], product?.defaultAreaSlug || DEFAULT_AREA_SLUG)
    : null;

  if (!productId || !bodyPart || !why) {
    return { displayText: text, recommendation: null };
  }

  const knownProduct = products == null ? true : Boolean(product);

  // Keep the structured line visible when we cannot resolve a card.
  if (!knownProduct) {
    return {
      displayText: text,
      recommendation: { productId, bodyPart, bodyParts, why, area },
    };
  }

  const displayText = text.replace(RECOMMENDATION_LINE, "\n").trimEnd();

  return {
    displayText,
    recommendation: { productId, bodyPart, bodyParts, why, area },
  };
}

export function slugifyBodyPart(label) {
  return (label || "")
    .toString()
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function resolveRecommendationCard(recommendation, catalog) {
  if (!recommendation || !catalog) {
    return null;
  }

  const products = catalog.products || catalog;
  const aliases = catalog.aliases || {};
  const productId = normalizeProductId(recommendation.productId, aliases);
  const product = products[productId];
  if (!product) {
    return null;
  }

  const bodyParts =
    Array.isArray(recommendation.bodyParts) && recommendation.bodyParts.length
      ? recommendation.bodyParts
      : parseBodyParts(recommendation.bodyPart, product.maxBodyParts);
  const bodyPart = bodyParts.join(BODY_PART_DISPLAY_JOINER);
  const area = normalizeArea(
    recommendation.area,
    product.defaultAreaSlug || DEFAULT_AREA_SLUG
  );

  const primaryCta = product.primaryCta || PRIMARY_CTA_BOOK;

  // Client preview only — keep the curated service page. Authoritative booking
  // / centres deep-links come from the server-resolved card on final bubbles.
  return {
    modality: product.modality,
    bodyPart,
    bodyParts,
    area,
    why: recommendation.why,
    fromPriceLabel: product.fromPriceLabel || null,
    bookPath: product.bookPath,
    centresPath: product.bookPath,
    productId,
    primaryCta,
  };
}
